<template>
  <v-container>
    <v-card>
      <v-card-title>
        Schedules
        <v-spacer></v-spacer>
        <v-text-field v-model="search" append-icon="mdi-magnify" label="Search" single-line hide-details></v-text-field>
      </v-card-title>
      <!-- Pending-changes bar: shows only while there are unsaved edits -->
      <v-slide-y-transition>
        <div v-if="dirtyRows.length > 0" class="d-flex align-center px-4 py-2 amber lighten-5">
          <span class="text-body-2 amber--text text--darken-4">
            {{ dirtyRows.length }} unsaved change{{ dirtyRows.length === 1 ? '' : 's' }}
          </span>
          <v-spacer />
          <v-btn text small class="mr-2" :disabled="isSubmitting" @click="undoAll">Undo all</v-btn>
          <v-btn small color="teal" class="white--text" :loading="isSubmitting" @click="submit">
            Submit ({{ dirtyRows.length }})
          </v-btn>
        </div>
      </v-slide-y-transition>
      <v-data-table :loading="isFetchingData" :headers="headers" :items="schedules" :item-class="rowClass"
        :search="search" item-key="id" class="elevation-1" :sort-by="['savedState', 'date_appointment_start']"
        :sort-desc="[true, false]" no-data-text="No scheduled appointments" fixed-header :height="tableHeight"
        disable-pagination hide-default-footer>
        <template #item.meeting_url="{ item }">
          <v-tooltip bottom v-if="item.meeting_url !== ''">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon aria-label="Open meeting link" v-bind="attrs" v-on="on"
                @click="goto_link(item.meeting_url)">
                <v-icon>mdi-link</v-icon>
              </v-btn>
            </template>
            <span>Open meeting link</span>
          </v-tooltip>
        </template>
        <template v-slot:item.scheduled_to="{ item }">
          <v-dialog v-model="item.profileDialog" width="600">
            <template v-slot:activator="{ on, attrs }">
              <v-btn text color="primary" class="pa-0 text-none text-capitalize" v-bind="attrs" v-on="on">
                {{ item.scheduled_to }}
              </v-btn>
            </template>
            <v-card shaped>
              <v-card>
                <v-img max-height="600" :src="item.scheduled_to_profile_image_url" cover></v-img>
                <v-card-title class="headline text-capitalize"> {{ item.scheduled_to }}</v-card-title>
                <v-card-text>
                  <v-list>
                    <v-list-item>
                      <v-list-item-icon>
                        <v-icon color="blue-grey">
                          mdi-phone
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title> {{ item.scheduled_to_contact_num }}</v-list-item-title>
                        <v-list-item-subtitle>Mobile</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-icon>
                        <v-icon color="blue-grey">
                          mdi-at
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title> {{ item.scheduled_to_email_add }}</v-list-item-title>
                        <v-list-item-subtitle>Email</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn text color="primary" @click="item.profileDialog = false">Close</v-btn>
                </v-card-actions>
              </v-card>
            </v-card>
          </v-dialog>
        </template>
        <!-- Editable state; a changed row is highlighted and committed on Submit -->
        <template v-slot:item.state="{ item }">
          <div class="d-flex align-center">
            <v-select :items="states" label="State" v-model="item.state" dense solo hide-details></v-select>
            <!-- Marks a row with an unsaved State change, pending Submit -->
            <v-chip v-if="item.state !== item.savedState" x-small color="amber darken-2" text-color="white"
              class="ml-2 flex-shrink-0">Unsaved</v-chip>
          </div>
        </template>
        <template v-slot:item.date_appointment="{ item }">
          <span>{{ item.formatted_date_appointment }}</span>
        </template>
        <template v-slot:item.client_schedules_count="{ item }">
          <v-dialog v-model="item.historyDialog" width="600">
            <template v-slot:activator="{ on, attrs }">
              <v-btn depressed rounded v-if="item.client_schedules_count > 0" outlined color="indigo" v-bind="attrs"
                v-on="on">
                {{ item.client_schedules_count }}
              </v-btn>
            </template>
            <v-card shaped>
              <v-card-title class="text-h5 grey lighten-2">
                Previous appointments
              </v-card-title>
              <v-card-text>
                <v-data-table :headers="prev_sched_headers" :items="item.client_schedules" item-key="start_date"
                  :sort-by="['start_date']" :sort-desc="[true]" hide-default-footer disable-pagination>
                </v-data-table>
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn text color="primary" @click="item.historyDialog = false">Close</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import moment from 'moment'

import { get_secretary_schedules, post_secretary_schedules } from '../api'
import store from '../store'
import logger, { describeApiError } from '../utils/logger'


export default {
  data() {
    return {
      isFetchingData: true,
      isSubmitting: false,
      search: '',
      states: [
        'Pending Approval',
        'Approved',
        'Cancelled'
      ],
      headers: [
        {
          text: 'Appointment Date',
          align: 'start',
          sortable: true,
          value: 'date_appointment',
          width: '15%'
        },
        { text: 'Patient', value: 'scheduled_to', width: '15%' },

        { text: 'Reason', value: 'reason', width: '25%' },
        { text: 'Service Provider', value: 'scheduled_by', width: '15%' },
        { text: 'Meeting URL', value: 'meeting_url', width: '5%' },
        { text: 'State', value: 'state', width: '15%', sortable: false },
        { text: 'Client previous appointments', value: 'client_schedules_count', width: '10%' }

      ],
      prev_sched_headers: [
        {
          text: 'Appointment Date',
          align: 'start',
          value: 'start_date_formatted',
          sortable: false,
        },
        { text: 'Reason', value: 'reason', },
        { text: 'Service Provider', value: 'provider', },
        { text: 'Role', value: 'role', },
      ],
      schedules: [], // Schedules sent
    }
  },
  computed: {
    // Rows whose State no longer matches the last backend-confirmed value
    dirtyRows: function () {
      return this.schedules.filter(s => s.state !== s.savedState)
    },
    // Fill the viewport below the app bar / card title / Submit bar,
    // with a floor so the table stays usable on short screens
    tableHeight: function () {
      return 'max(360px, calc(100vh - 320px))'
    }
  },
  methods: {
    notify: function (text, color) {
      store.dispatch('notification/notify', { text, color })
    },
    // Highlight rows with unsaved State changes
    rowClass: function (item) {
      return item.state !== item.savedState ? 'dirty-row' : ''
    },
    // Revert every row with an unsaved edit back to its last saved status
    undoAll: function () {
      this.schedules.forEach(s => { if (s.state !== s.savedState) s.state = s.savedState })
    },
    submit: function () {
      const dirty = this.dirtyRows
      if (!dirty.length) {
        this.notify('No changes to submit', 'info')
        return
      }

      this.isSubmitting = true
      const updated = dirty.map(s => ({ id: s.id, updated_state: s.state }))

      const jwt = store.getters.getJwt
      post_secretary_schedules({
        'schedules': updated
      }, jwt).then(() => {
        // Rebase committed rows to their new value so highlight + count clear
        // in place, no reload — search and sort are preserved
        dirty.forEach(row => { row.savedState = row.state })
        this.notify(`${updated.length} schedule(s) updated`, 'success')
      }).catch((error) => {
        // Leave rows dirty on failure so no change is lost
        logger.error('Secretary', 'Failed to update schedules', describeApiError(error))
        this.notify('Failed to update schedules — please try again', 'error')
      }).finally(() => {
        this.isSubmitting = false
      })
    },
    goto_link: function (link) {
      window.open(link, '_blank', 'noopener')
    }
  },
  mounted() {
    const jwt = store.getters.getJwt
    get_secretary_schedules(jwt).then(response => {
      for (const schedule of response.data.schedules) {
        // Backend dates are UTC; format to local time for display
        schedule.formatted_date_appointment = moment(schedule.date_appointment_start).format('lll')
        schedule.client_schedules_count = schedule.client_schedules.length
        // Seed reactive fields before push so Vue 2 tracks them:
        // savedState is the dirty-check baseline; dialog flags drive the modals
        schedule.savedState = schedule.state
        schedule.profileDialog = false
        schedule.historyDialog = false

        schedule.client_schedules = schedule.client_schedules.map(cs => ({
          start_date: cs.start,
          start_date_formatted: moment(cs.start).format('lll'),
          reason: cs.reason,
          provider: cs.provider,
          role: cs.provider_role,
        }))

        this.schedules.push(schedule)
      }
    }).catch((error) => {
      logger.error('Secretary', 'Failed to load schedules', describeApiError(error))
      this.notify('Failed to load schedules — please refresh the page', 'error')
    }).finally(() => {
      // Always clear the spinner, even if the fetch failed
      this.isFetchingData = false
    })
  },
}
</script>

<style scoped>
/* Rows with an unsaved State change, pending Submit */
.dirty-row {
  background-color: #fff8e1;
}
</style>
